import { PlusOutlined } from "@ant-design/icons"
import { DndContext, type DragEndEvent } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { Alert, Button, Col, Empty, Flex, Row, Typography } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { type LeadFormDataPhase } from "../../LeadAddPage/components/lead-form-data.ts"
import { LeadScopeMilestoneRow } from "./LeadScopeMilestoneRow.tsx"
import { LeadScopeMilestoneTimelineAlert } from "./LeadScopeMilestoneTimelineAlert.tsx"
import {
  columnFlex,
  stretchColumnFlex,
  MILESTONE_EDITOR_COLUMN_WIDTHS as widths
} from "./lead-scope-milestone-layout.ts"
import {
  type PlanningWindow,
  milestoneEndsBeforeStart,
  milestonesTimelineWarnings
} from "./lead-scope-milestone-timeline.ts"
import {
  createMilestone,
  isMilestoneNamed,
  milestonesDurationTotal,
  milestonesTotal,
  moveMilestone
} from "./lead-scope-milestones.ts"

const { Text } = Typography

interface LeadScopeMilestonesEditorProps {
  milestones: LeadFormDataPhase[]
  activeProjectTypeId?: string | null
  filterByProjectType?: boolean
  defaultProjectTypeId?: string | null
  salesPrice?: number | null
  planning?: PlanningWindow
  saving?: boolean
  onChange: (milestones: LeadFormDataPhase[]) => void
}

function LeadScopeMilestonesEditor({
  milestones,
  activeProjectTypeId,
  filterByProjectType,
  defaultProjectTypeId,
  salesPrice,
  planning = {},
  saving,
  onChange
}: LeadScopeMilestonesEditorProps) {
  const { t } = useTranslation()
  const { normalizeDate } = useDateFormat()

  const visibleMilestones = useMemo(
    () =>
      filterByProjectType
        ? milestones.filter(
            (milestone) => milestone.project_type === activeProjectTypeId
          )
        : milestones,
    [activeProjectTypeId, filterByProjectType, milestones]
  )

  const move = (from: number, to: number) => {
    const moved = moveMilestone(visibleMilestones, from, to)

    if (!filterByProjectType) {
      onChange(moved)
      return
    }

    let activeIndex = 0
    onChange(
      milestones
        .map((milestone) =>
          milestone.project_type === activeProjectTypeId
            ? moved[activeIndex++]
            : milestone
        )
        .map((milestone, index) => ({ ...milestone, order: index }))
    )
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return

    const from = visibleMilestones.findIndex((row) => row.id === active.id)
    const to = visibleMilestones.findIndex((row) => row.id === over.id)
    if (from === -1 || to === -1) return

    move(from, to)
  }

  const addMilestone = () =>
    onChange([
      ...milestones,
      createMilestone(milestones.length, defaultProjectTypeId)
    ])

  const durationTotal = milestonesDurationTotal(visibleMilestones)
  const totalPrice = milestonesTotal(milestones)
  const parsedSalesPrice = Number(salesPrice)
  const hasSalesPrice =
    Number.isFinite(parsedSalesPrice) && parsedSalesPrice > 0
  const salesPriceMismatch =
    milestones.length > 0 &&
    hasSalesPrice &&
    Math.abs(totalPrice - parsedSalesPrice) > 0.005
  const salesPriceDirection =
    totalPrice > parsedSalesPrice ? "Exceeds" : "Below"
  const timelineWarnings = milestonesTimelineWarnings(
    milestones,
    planning,
    (value) => normalizeDate(value)
  )
  const timelineIssuesOf = new Map(
    timelineWarnings.map(({ milestone, issues }) => [milestone.id, issues])
  )

  return (
    <Flex vertical gap={"small"}>
      {salesPriceMismatch && (
        <Alert
          showIcon
          type={"warning"}
          data-testid={"milestones-sales-price-alert"}
          message={t(
            `leadWorkspace.scope.milestones.totalPrice${salesPriceDirection}SalesPrice`,
            { total: totalPrice, salesPrice: parsedSalesPrice }
          )}
        />
      )}
      <LeadScopeMilestoneTimelineAlert
        warnings={timelineWarnings}
        planning={planning}
      />
      <div
        className={"grid w-full overflow-auto"}
        data-testid={"milestones-editor-container"}
      >
        <Flex
          className={"border-b border-border rounded-t-card bg-primary/10 py-1"}
        >
          <Row className={"flex-nowrap flex-1"} gutter={4}>
            <Col flex={columnFlex(widths.actions)} />
            <Col flex={stretchColumnFlex(widths.name)} className={"font-bold"}>
              {t("leadWorkspace.scope.milestones.columns.name")}
            </Col>
            <Col flex={columnFlex(widths.date)} className={"font-bold"}>
              {t("leadWorkspace.scope.milestones.columns.start")}
            </Col>
            <Col flex={columnFlex(widths.date)} className={"font-bold"}>
              {t("leadWorkspace.scope.milestones.columns.end")}
            </Col>
            <Col flex={columnFlex(widths.duration)} className={"font-bold"}>
              {t("leadWorkspace.scope.milestones.columns.duration")}
            </Col>
            <Col flex={columnFlex(widths.money)} className={"font-bold"}>
              {t("leadWorkspace.scope.milestones.columns.price")}
            </Col>
            <Col flex={columnFlex(widths.remove)} />
          </Row>
        </Flex>

        {visibleMilestones.length ? (
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={visibleMilestones.map((row) => row.id)}
              strategy={verticalListSortingStrategy}
            >
              {visibleMilestones.map((milestone, index) => (
                <LeadScopeMilestoneRow
                  key={milestone.id}
                  milestone={milestone}
                  index={index}
                  total={visibleMilestones.length}
                  saving={saving}
                  planning={planning}
                  timelineIssues={timelineIssuesOf.get(milestone.id)}
                  nameError={
                    isMilestoneNamed(milestone)
                      ? null
                      : t("leadWorkspace.scope.milestones.nameRequired")
                  }
                  endError={
                    milestoneEndsBeforeStart(milestone, (value) =>
                      normalizeDate(value)
                    )
                      ? t("leadWorkspace.scope.milestones.endBeforeStart")
                      : null
                  }
                  onChange={(_, next) =>
                    onChange(
                      milestones.map((row) =>
                        row.id === milestone.id ? next : row
                      )
                    )
                  }
                  onMove={move}
                  onRemove={() =>
                    onChange(
                      milestones.filter((row) => row.id !== milestone.id)
                    )
                  }
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("leadWorkspace.scope.milestones.empty")}
          />
        )}

        <Flex
          className={"border-t border-border rounded-b-card bg-primary/10 py-1"}
        >
          <Row className={"flex-nowrap flex-1"} gutter={4}>
            <Col flex={columnFlex(widths.actions)} />
            <Col flex={stretchColumnFlex(widths.name)} className={"text-end"}>
              <Text strong>{t("leadWorkspace.scope.milestones.total")}</Text>
            </Col>
            <Col flex={columnFlex(widths.date)} />
            <Col flex={columnFlex(widths.date)} />
            <Col flex={columnFlex(widths.duration)}>
              <Text strong data-testid={"milestones-duration-total-edit"}>
                {durationTotal
                  ? t("leadWorkspace.scope.milestones.durationDays", {
                      count: durationTotal
                    })
                  : "—"}
              </Text>
            </Col>
            <Col flex={columnFlex(widths.money)}>
              <Text strong data-testid={"milestones-total-edit"}>
                {budgetFormat(milestonesTotal(visibleMilestones))}
              </Text>
            </Col>
            <Col flex={columnFlex(widths.remove)} />
          </Row>
        </Flex>
      </div>

      <Button
        type={"dashed"}
        icon={<PlusOutlined />}
        disabled={saving}
        aria-label={t("leadWorkspace.scope.milestones.add")}
        onClick={addMilestone}
      >
        {t("leadWorkspace.scope.milestones.add")}
      </Button>
    </Flex>
  )
}

export { LeadScopeMilestonesEditor }
